import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IMenu, IMenuSeqReq } from '../../../models/menus.model';
import { doGetMenus, doUpdateMenuSeq } from '../../../store/menu.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import _ from 'lodash';

import styled from 'styled-components';
import { useSnackbar } from 'notistack';
import { Table, TableColumnsType } from 'antd';


import Loader from '../../../components/Loader';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Footer from '../../../components/layout/Footer';
import Button from '../../../components/Button';
import { errorVariant, successVariant, warningVariant } from '../../../lib/barVariant';
import ConfirmModal from '../../../components/modals/ConfirmModal';
import useAxios from '../../../hooks/useAxios';
import { deleteMenu } from '../../../api/menus.api';
import useNotification from '../../../hooks/useNotification';
import ErrorResponse from '../../../components/ErrorResponse';
import useAuth from '../../../hooks/useAuth';

const Block = styled.div`
  .button {
    .disable > img {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

const SystemMenu: FC<RouteComponentProps> = ({ history }) => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { isDarkMode } = useSelector((state: RootState) => state.auth);
  const { menus, isLoading } = useSelector((state: RootState) => state.menu);
  const { successNotification } = useNotification();
  const { data: deleteRes, isLoading: deleteIsLoading, error: deleteError, execute: deleteExecute } = useAxios();

  const [menuData, setMenuData] = useState<IMenu[]>([]);
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const [menuCode, setMenuCode] = useState<number>();

  useEffect(() => {
    getMenus();
  }, []);

  useEffect(() => {
    if (menus) {
      const _menus = _.cloneDeep(menus);
      setMenuData(sortMenu(_menus));
    }
  }, [menus]);

  useEffect(() => {
    if (deleteRes && !deleteIsLoading) {
      getMenus();
      setConfirmDeleteVisible(false);
      successNotification(t('common.message.delete'));
    }
  }, [deleteRes, deleteIsLoading]);

  const sortMenu = (menus: IMenu[]) => {
    menus.sort((a, b) => a.menuSeq < b.menuSeq ? -1 : (a.menuSeq > b.menuSeq ? 1 : 0));
    menus.forEach((item, index) => {
      item.menuSeq = index + 1;
    });

    return menus;
  }

  const raiseMenu = (menuSeq: number) => {
    const _menu = [...menuData];
    const index = _menu.findIndex(menu => menu.menuSeq === menuSeq);
    _menu[index].menuSeq--;
    _menu[index - 1].menuSeq++;
    setMenuData(sortMenu(_menu));
  }

  const lowerMenu = (menuSeq: number) => {
    const _menu = [...menuData];
    const index = _menu.findIndex(menu => menu.menuSeq === menuSeq);
    _menu[index].menuSeq++;
    _menu[index + 1].menuSeq--;
    setMenuData(sortMenu(_menu));
  }

  const getMenus = async () => {
    const res = await dispatch(doGetMenus());

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      case 404:
        enqueueSnackbar(t('common.message.notFound'), warningVariant);
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  const saveSequence = async () => {
    const seqData: IMenuSeqReq[] = [];
    menuData.forEach(menu => {
      seqData.push({
        menuCode: menu.menuCode,
        menuSeq: menu.menuSeq
      })
    })

    const res = await dispatch(doUpdateMenuSeq(seqData));

    switch (res?.status) {
      case 200:
        enqueueSnackbar(t('common.message.save'), successVariant);
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  const onDelete = () => {
    if (!menuCode) return;
    deleteExecute(deleteMenu(menuCode));
  }

  const columns: TableColumnsType<IMenu> = [
    {
      title: t('system.menu.column1'),
      dataIndex: 'menuSeq',
      key: 'menuSeq',
      sorter: (a, b) => a.menuSeq - b.menuSeq,
    },
    {
      title: t('system.menu.column2'),
      dataIndex: 'korName',
      key: 'korName',
      sorter: (a, b) => a.korName?.localeCompare(b.korName)
    },
    {
      title: t('system.menu.column3'),
      dataIndex: 'engName',
      key: 'engName',
      sorter: (a, b) => a.engName?.localeCompare(b.engName)
    },
    {
      title: t('system.menu.column4'),
      dataIndex: 'menuSeq',
      key: 'menuSeq',
      render: (menuSeq: number) => (
        <div className="order-sett__area">
          <a
            className={menuSeq === 1 ? 'btn-order up disabled' : 'btn-order up'}
            onClick={() => {
              if (menuSeq === 1) return;
              raiseMenu(menuSeq);
            }}
          />
          <a
            className={menuSeq === menuData.length ? 'btn-order down disabled' : 'btn-order down'}
            onClick={() => {
              if (menuSeq === menuData.length) return;
              lowerMenu(menuSeq);
            }}
          />
        </div>
      ),
    },
    {
      title: t('system.menu.column5'),
      key: 'menuUrl',
      dataIndex: 'menuUrl',
      sorter: (a, b) => a.menuUrl?.localeCompare(b.menuUrl)
    },
    {
      title: t('system.menu.column6'),
      key: 'iconName',
      dataIndex: 'iconName',
      sorter: (a, b) => a.iconName?.localeCompare(b.iconName)
    },
    {
      title: '',
      key: 'menuCode',
      dataIndex: 'menuCode',
      render: (value, record) => (
        <div className='comm-btn__area justify-content-center'>
          <Button
            className='btn-basic small gray'
            onClick={() => {
              setMenuCode(value);
              setConfirmDeleteVisible(true);
            }}
            text={t('common.button.delete')}
            writeAuthYn={writeAuthYn}
          />
          <Button
            className='btn-basic small pink-line'
            onClick={() => history.push(`/system/menu/${value}/edit`)}
            text={t('common.button.edit')}
            writeAuthYn={writeAuthYn}
          />
          <Button
            className='btn-basic mint'
            onClick={() => {
              sessionStorage.setItem('TopMenu_ko', record.korName);
              sessionStorage.setItem('TopMenu_en', record.engName);
              history.push(`/system/menu/${value}/submenu`)
            }}
            text={t('system.menu.button1')}
          />
        </div>
      ),
    },
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-system-big.svg"
      parentMenu={t('common.menu.system')}
      currentMenu={t('system.menu.title1')}
      onlyTitle={true}
    />    
    <Block className="content scrollbar">
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={deleteError} />

      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">
            <h5 className='sub-title'>{t('system.menu.text1')}</h5>
          </div>
          <div className="comm-btn__area">
            <Button
              className='btn-basic pink'
              onClick={saveSequence}
              text={t('system.menu.button2')}
              writeAuthYn={writeAuthYn}
            />
            <Button
              className='btn-basic pink-line'
              onClick={() => history.push('/system/menu/create')}
              text={t('system.menu.button4')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
        <div className="comm-table__area">
          <Table
            columns={columns}
            dataSource={menuData}
            pagination={{ pageSize: 99999 }}
          />
        </div>
        <div className="comm-bx__space">
          <div className="comm-btn__area right">
            <Button
              className='btn-basic pink'
              onClick={saveSequence}
              text={t('system.menu.button2')}
              writeAuthYn={writeAuthYn}
            />
            <Button
              className='btn-basic pink-line'
              onClick={() => history.push('/system/menu/create')}
              text={t('system.menu.button4')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>
      <Footer />

      {confirmDeleteVisible && (
        <ConfirmModal
          visible={confirmDeleteVisible}
          setVisible={setConfirmDeleteVisible}
          onConfirm={onDelete}
          titleText={t('system.menu.title6')}
          messageText={t('system.menu.text3')}
          confirmText={t('common.button.delete')}
          cancelText={t('common.button.cancel')}
        />
      )}
    </Block>
    </>
  )
}

export default SystemMenu;
